// components/CookieConsent.js — consent banner + settings panel.
// Shown once per browser; choice is remembered for CONSENT_MAX_AGE_DAYS.
// "Reject all" is as prominent as "Accept all" (CNIL / EDPB requirement).
import React, { useEffect, useState } from 'react';
import {
  getConsent, setConsent, onConsentChange, loadGoogleAnalytics,
  gpcEnabled, OPEN_SETTINGS_EVENT,
} from '../utils/consent';

const btnBase = 'px-4 py-2 rounded-md text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-sky-400';
const btnEqual = `${btnBase} border border-[#28445F] text-slate-100 bg-[#13283C] hover:bg-[#1C3348]`;

const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      if (gpcEnabled()) setConsent({ analytics: false, source: 'gpc' });
      else setOpen(true);
    } else {
      setAnalytics(!!existing.analytics);
    }
    const unsub = onConsentChange((c) => { if (c.analytics) loadGoogleAnalytics(); });
    const reopen = () => {
      const c = getConsent();
      setAnalytics(!!(c && c.analytics));
      setCustom(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, reopen);
    return () => { unsub(); window.removeEventListener(OPEN_SETTINGS_EVENT, reopen); };
  }, []);

  if (!open) return null;

  const save = (value) => { setConsent({ analytics: value }); setOpen(false); setCustom(false); };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="ts-cookie-title"
      className="fixed inset-x-0 bottom-0 z-[1000] p-3 sm:p-4"
    >
      <div className="max-w-3xl mx-auto rounded-md border border-[#28445F] bg-[#0D1E2E] p-4 sm:p-5 text-slate-300">
        <h2 id="ts-cookie-title" className="text-white font-semibold mb-1">Your privacy choices</h2>
        <p className="text-sm leading-relaxed">
          We use strictly necessary storage to run the site and keep you signed in. With your permission we also use
          analytics (Google Analytics and our own visitor statistics) to understand how the site is used. You can change
          your choice at any time from &ldquo;Cookie settings&rdquo; in the footer.{' '}
          <a href="/legal#cookies" className="underline text-sky-300 hover:text-sky-200">Cookie Policy</a>
        </p>

        {custom && (
          <div className="mt-4 space-y-3">
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-[#07131F] border border-[#1C3348]">
              <div>
                <p className="text-white text-sm font-semibold">Strictly necessary</p>
                <p className="text-xs text-slate-400">Sign-in, security, payments, your saved preferences. Always on.</p>
              </div>
              <span className="text-xs text-slate-400 whitespace-nowrap">Always on</span>
            </div>
            <label className="flex items-start justify-between gap-4 p-3 rounded-lg bg-[#07131F] border border-[#1C3348] cursor-pointer">
              <div>
                <p className="text-white text-sm font-semibold">Analytics</p>
                <p className="text-xs text-slate-400">Google Analytics and anonymous visitor statistics. Off unless you turn it on.</p>
              </div>
              <input
                type="checkbox"
                className="mt-1 h-5 w-5 accent-sky-400"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                aria-label="Allow analytics"
              />
            </label>
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
          {custom ? (
            <button className={btnEqual} onClick={() => save(analytics)}>Save choices</button>
          ) : (
            <button className={btnEqual} onClick={() => setCustom(true)}>Customize</button>
          )}
          <button className={btnEqual} onClick={() => save(false)}>Reject all</button>
          <button className={btnEqual} onClick={() => save(true)}>Accept all</button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
